import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import type { FeedbackFormComponentConfig, FlowValues } from '../types';
import HtmlText from '../components/HtmlText';

export function FeedbackRecoveryFields({
  forms,
  values,
  selection,
  reasons,
  reasonsLabel,
  maxLength,
  pending,
  onChange,
}: {
  forms: FeedbackFormComponentConfig[];
  values: FlowValues;
  selection: 'single' | 'multiple';
  reasons: { id: string; label: string }[];
  reasonsLabel: string;
  maxLength: number;
  pending: boolean;
  onChange: (componentId: string, value: unknown) => void;
}) {
  return (
    <Stack spacing={2}>
      {forms.map((form) => {
        const value = (values[form.id] ?? {}) as {
          notes?: string;
          tags?: string[];
          submitted?: boolean;
        };
        const tags = Array.isArray(value.tags) ? value.tags : [];
        const options = reasons.length
          ? reasons
          : (form.tags ?? []).map((tag) => ({ id: tag, label: tag }));
        const locked = pending || (value.submitted === true && form.allowResubmit === false);
        return (
          <Stack
            key={form.id}
            spacing={1}
            data-feedback-field={form.id}
            data-component-id={form.id}
          >
            {form.title && <HtmlText html={form.title} variant="subtitle2" />}
            {form.inputMode !== 'text' && (
              <Stack
                role="group"
                aria-label={reasonsLabel}
                direction="row"
                sx={{
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  columnGap: 0.75,
                  rowGap: 0,
                  width: '100%',
                }}
              >
                {options.map(({ id: tag, label }) => (
                  <Chip
                    key={tag}
                    label={label}
                    disabled={locked}
                    aria-pressed={tags.includes(tag)}
                    onClick={() =>
                      onChange(form.id, {
                        ...value,
                        submitted: false,
                        tags: tags.includes(tag)
                          ? tags.filter((item) => item !== tag)
                          : selection === 'single'
                            ? [tag]
                            : [...tags, tag],
                      })
                    }
                    sx={{
                      // Keep a 44px interaction target around the compact Figma pill.
                      minHeight: 44,
                      height: 'auto',
                      maxWidth: '100%',
                      position: 'relative',
                      isolation: 'isolate',
                      borderRadius: '36px',
                      fontSize: 14,
                      fontWeight: 600,
                      bgcolor: 'transparent',
                      color: tags.includes(tag)
                        ? 'var(--cx-brand-text)'
                        : 'var(--cx-brand-text-muted)',
                      border: 0,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: '4px 0',
                        borderRadius: '36px',
                        bgcolor: tags.includes(tag)
                          ? 'var(--cx-brand-primary-soft)'
                          : 'var(--cx-brand-surface)',
                        border: tags.includes(tag)
                          ? '2px solid var(--cx-brand-text)'
                          : '1px solid var(--cx-brand-border)',
                        zIndex: -1,
                        pointerEvents: 'none',
                      },
                      // Pointer effects must stay inside the painted pill, not its larger hit area.
                      '& .MuiTouchRipple-root': { display: 'none' },
                      '&:active': { boxShadow: 'none' },
                      '& .MuiChip-label': {
                        px: 1.25,
                        py: '8px',
                        lineHeight: '20px',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        textAlign: 'center',
                      },
                      '&:hover, &.Mui-focusVisible': {
                        bgcolor: 'transparent',
                        '&::before': { borderColor: 'var(--cx-brand-text)' },
                      },
                      '&.Mui-focusVisible::before': {
                        outline: '2px solid var(--cx-brand-text)',
                        outlineOffset: 1,
                      },
                    }}
                  />
                ))}
              </Stack>
            )}
            {form.inputMode !== 'tags' && (
              <TextField
                disabled={locked}
                fullWidth
                multiline
                rows={3}
                placeholder={form.placeholder}
                value={typeof value.notes === 'string' ? value.notes : ''}
                slotProps={{
                  // Native scrolling avoids autosize measurement and cursor writes
                  // while the mobile keyboard is changing the feedback sheet layout.
                  input: { inputComponent: 'textarea' },
                  htmlInput: {
                    maxLength,
                    'aria-label': form.placeholder || form.title || 'Feedback',
                  },
                }}
                onChange={(event) =>
                  onChange(form.id, { ...value, submitted: false, notes: event.target.value })
                }
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--cx-brand-border)' },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--cx-brand-border)',
                  },
                  '& textarea': { resize: 'vertical', overflowY: 'auto' },
                  '& .MuiInputBase-input::placeholder': { color: 'var(--cx-brand-text-muted)', opacity: 1 },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--cx-brand-text)',
                  },
                  '& .MuiInputBase-root': {
                    fontSize: 15,
                    bgcolor: 'var(--cx-brand-bg)',
                    color: 'var(--cx-brand-text)',
                    borderRadius: '8px',
                  },
                }}
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
